import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Ogni ora' },
  { value: 'every_4h', label: 'Ogni 4 ore' },
  { value: 'daily', label: 'Ogni giorno' },
  { value: 'weekly', label: 'Ogni settimana' },
];

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryEmail, setNewCategoryEmail] = useState('');
  const [newCategoryFrequency, setNewCategoryFrequency] = useState('hourly');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
      toast.error('Errore nel caricamento delle categorie');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName || !newCategoryEmail) {
      toast.error('Compila tutti i campi');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/categories`, {
        name: newCategoryName,
        email_recipient: newCategoryEmail,
        email_frequency: newCategoryFrequency,
      });
      toast.success('Categoria creata!');
      setNewCategoryName('');
      setNewCategoryEmail('');
      setNewCategoryFrequency('hourly');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    try {
      await axios.delete(`${API_URL}/categories/${categoryName}`);
      toast.success('Categoria eliminata');
      fetchCategories();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getFrequencyLabel = (frequency) => {
    const option = FREQUENCY_OPTIONS.find(opt => opt.value === frequency);
    return option?.label || 'Ogni ora';
  };

  return (
    <div className="flex-1 p-4 md:p-8 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto space-y-8"
      >
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Categorie</h1>
          <p className="text-muted-foreground">
            Gestisci le tue categorie e configura dove e quando inviare le note via email
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Crea nuova categoria</CardTitle>
            <CardDescription>
              Ogni categoria può avere un destinatario email e frequenza di invio diversi
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category-name">Nome categoria</Label>
                  <Input
                    id="category-name"
                    placeholder="Es: Lavoro, Personale..."
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="h-12"
                    data-testid="category-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category-email">Email destinatario</Label>
                  <Input
                    id="category-email"
                    type="email"
                    placeholder="email@esempio.com"
                    value={newCategoryEmail}
                    onChange={(e) => setNewCategoryEmail(e.target.value)}
                    className="h-12"
                    data-testid="category-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category-frequency">Frequenza invio email</Label>
                <Select value={newCategoryFrequency} onValueChange={setNewCategoryFrequency}>
                  <SelectTrigger className="h-12" data-testid="category-frequency-select">
                    <SelectValue placeholder="Seleziona frequenza" />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto rounded-full"
                data-testid="create-category-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crea categoria
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">Le tue categorie</h2>
          {categories.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Nessuna categoria. Creane una per iniziare!
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((cat, index) => (
                <motion.div
                  key={cat.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="hover:shadow-lg transition-shadow" data-testid={`category-card-${cat.name}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-3 flex-1">
                          <h3 className="text-xl font-semibold">{cat.name}</h3>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="w-4 h-4" />
                              <span>{cat.email_recipient}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="w-4 h-4" />
                              <span>{getFrequencyLabel(cat.email_frequency)}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteCategory(cat.name)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`delete-category-${cat.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}